import { useMemo } from 'react';
import type { Extra, Venue, Wardrobe, WeddingEvent } from './content';
import { useSchedule, useVenues } from './content';
import { dayKey, fmtDay, fmtDayLong, now } from './time';
import { stateOf } from '../components/EventCard';
import type { EventState } from '../components/EventCard';

export interface DayGroup {
  key: string;
  /** "Friday 6 Nov" for the phone, "Friday 6 November" for the laptop. */
  label: string;
  longLabel: string;
  isToday: boolean;
  events: WeddingEvent[];
}

export interface ScheduleModel {
  loading: boolean;
  error: Error | null;
  fetchedAt: number | null;
  at: number;
  events: WeddingEvent[];
  /** Not part of the programme, and never mixed into `events` — see `Extra`. */
  extras: Extra[];
  days: DayGroup[];
  venueOf: (id: string) => Venue | undefined;
  /** Running right now, if anything is. */
  current: WeddingEvent | null;
  /** The next one that has not started. This is the answer to the only question. */
  next: WeddingEvent | null;
  stateOf: (event: WeddingEvent) => EventState;
  wardrobe: Wardrobe | null;
}

/** One place that decides what is next, so Now, Schedule and the map agree. */
export function useScheduleModel(): ScheduleModel {
  const schedule = useSchedule();
  const venues = useVenues();
  const at = now();

  return useMemo(() => {
    const events = [...(schedule.data?.events ?? [])].sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
    const venueMap = new Map((venues.data?.venues ?? []).map((v) => [v.id, v]));

    const current = events.find((e) => stateOf(e, at) === 'now') ?? null;
    const next = events.find((e) => stateOf(e, at) === 'later') ?? null;

    const todayKey = dayKey(at);
    const days: DayGroup[] = [];
    for (const event of events) {
      const key = dayKey(event.start);
      let group = days.find((d) => d.key === key);
      if (!group) {
        group = { key, label: fmtDay(event.start), longLabel: fmtDayLong(event.start), isToday: key === todayKey, events: [] };
        days.push(group);
      }
      group.events.push(event);
    }

    return {
      loading: !schedule.data && !schedule.error,
      error: schedule.error,
      fetchedAt: schedule.fetchedAt,
      at,
      events,
      extras: schedule.data?.extras ?? [],
      days,
      venueOf: (id: string) => venueMap.get(id),
      current,
      next,
      stateOf: (event: WeddingEvent): EventState => {
        const base = stateOf(event, at);
        return base === 'later' && event.id === next?.id ? 'next' : base;
      },
      wardrobe: schedule.data?.wardrobe ?? null,
    } as ScheduleModel;
  }, [schedule.data, schedule.error, schedule.fetchedAt, venues.data, at]);
}
